import { colorToCss } from "../color/color";
import { CroppingStyle } from "../crops";
import { FieldViewModel, getCropName, getFieldCroppingArea, ICropping, IField } from "../fields";
import { FieldAreaType } from "../generated/enums";
import { checkDateIntersection } from "../utils/date";
import { appendAreaHa } from "../utils/format";
import { getCroppingSiblings } from "./history";
import { getPerennialSequence } from "./perennial";

export class FieldViewModelImpl implements FieldViewModel {
    private _uid: string;

    readonly field: IField;
    readonly croppings: FieldViewModel[];

    readonly cropping: ICropping;
    readonly fieldVm: FieldViewModel;

    readonly currentYear: boolean;
    readonly disabled: boolean;

    readonly authorized: boolean = true

    readonly ignoreBoundary: boolean;

    private _siblings: ICropping[];

    private _style: CroppingStyle;

    selected: boolean = false;
    visible: boolean;

    constructor(
        item: IField | ICropping,
        currentYear: number,
        authorizedCroppingFilter: (croppiingId: number) => boolean,
        parentFieldVm?: FieldViewModel
    ) {

        if (parentFieldVm) {

            const cropping = <ICropping>item;

            this.cropping = cropping;
            this.field = this.cropping.Field;
            this._uid = this.field.FieldId + "-" + this.cropping.CroppingId;
            this.fieldVm = parentFieldVm;
            this._siblings = getCroppingSiblings(this.cropping, false);
            this.currentYear = cropping.YearId == currentYear;
            this.disabled = parentFieldVm.isDisabledOnYear && parentFieldVm.isDisabledOnYear(cropping.YearId);
            this.authorized = authorizedCroppingFilter(cropping.CroppingId)
            this.ignoreBoundary = parentFieldVm.ignoreBoundary;
            this._style = cropping.Style;

        } else {

            const field = <IField>item;

            this.field = field;
            this.fieldVm = this;
            this._uid = this.field.FieldId + "";
            this.croppings = (this.field.Cropping || []).map((c) => new FieldViewModelImpl(c, currentYear, authorizedCroppingFilter, this));
            this.disabled = field.Years && field.Years.some((y) => y.Disabled && y.YearId == currentYear);
            this.ignoreBoundary = this.disabled && field.IgnoreBoundary;

        }
    }

    get fieldName() {
        return this.field.Name;
    }

    get fieldGroup() {
        return this.field.FieldGroup;
    }

    get yearId() {
        return this.cropping && this.cropping.YearId;
    }

    is(cropping: boolean, id: number): boolean {
        return cropping == this.isCropping() && (cropping ? this.croppingId : this.fieldId) == id;
    }

    isField(): boolean {
        return !this.cropping;
    }

    isCropping(): boolean {
        return !!this.cropping;
    }

    getSameYearCrops() {
        return this.fieldVm && this.isCropping()
            ? this.fieldVm.croppings.filter((c) => c.yearId == this.yearId)
            : undefined;
    }

    getSiblings() {
        return [...this._siblings];
    }

    hasCrops(): boolean {
        return this.croppings && !!this.croppings.length;
    }

    hasSiblings(): boolean {
        return this._siblings && !!this._siblings.length;
    }

    getId(): string {
        return this._uid;
    }

    get description(): string {
        return this.getDescription(false);
    }

    get shortDescription(): string {
        return this.getDescription(true);
    }

    getDescription(short: boolean = false): string {
        if (this.isField()) {
            const { field, area } = this;

            let d = field.Name;

            if (area) {
                d = appendAreaHa(d, area);
            }

            return d;
        }

        if (this.isCropping()) {
            return this.getCroppingDescription(!short, false, true, !short);
        }
    }

    getFieldName() {
        return this.isField() ? this.field?.Name : this.cropping?.Field?.Name;
    }

    getCroppingDescription(
        includeFieldName: boolean = true,
        includeVariety: boolean = false,
        includeCrop: boolean = true,
        includePlotId: boolean = true
    ) {
        const { area } = this;

        let name = [
            includeFieldName ? this.getFieldName() : undefined,
            includePlotId ? this.cropping?.PlotId : undefined,
            includeCrop ? this.getCropName() : undefined,
            includeVariety ? this.getVarietyName() : undefined,
        ]
            .filter(Boolean)
            .join(" / ");

        if (area) {
            return appendAreaHa(name, area);
        }

        return name;
    }

    getBoundary() {
        const src = this.cropping || this.field;

        if (src && src.Boundary) {
            return src.Boundary;
        }

        return null;
    }

    hasBoundary(): boolean {
        return !!this.getBoundary();
    }

    useBoundaryAea(): boolean {
        return this.field && this.field.AreaType == FieldAreaType.BoundaryArea;
    }

    isUsedInPlans(): boolean {
        if (this.cropping) {
            return this.cropping.InSeedsPlan || this.cropping.InNutritionPlan;
        } else if (this.croppings) {
            return this.croppings.some((c) => c.isUsedInPlans());
        }
    }

    getCropName(): string {
        return getCropName(this.cropping);
    }

    get cropDescription() {
        return this.cropping?.CropDescr;
    }

    getVarietyName() {
        return getVarietyName(this.cropping);
    }

    get area(): number {
        if (this.isCropping()) {
            return this.cropping.Area;
        }

        return getFieldCroppingArea(this.field);
    }

    get areaType() {
        return this.field.AreaType;
    }

    get fieldId(): number {
        return this.field.FieldId;
    }

    get croppingId(): number {
        if (this.isCropping()) return this.cropping.CroppingId;
    }

    get cropId(): number {
        return this.cropping ? this.cropping.CropId : undefined;
    }

    get varietyId() {
        return this.cropping ? this.cropping.VarietyId : undefined;
    }

    get variety() {
        return this.cropping ? this.cropping.Variety : undefined;
    }

    get marketId() {
        const { cropping, variety } = this;
        return cropping?.Market || variety?.Market;
    }

    get crop() {
        return this.cropping ? this.cropping.Crop : undefined;
    }

    get uncropped(): boolean {
        if (this.isCropping()) {
            return !this.cropping.CropId;
        }
    }

    get startDate() {
        const { cropping: c } = this;
        return c ? c.StartDate : undefined;
    }

    get endDate() {
        const { cropping: c } = this;
        return c ? c.EndDate : undefined;
    }

    get perennial() {
        const crop = this.crop;
        return crop && crop.Perennial;
    }

    getPerennialSequence() {
        if (this.cropping) {
            return getPerennialSequence(this.cropping);
        }
    }

    get style() {
        return this._style;
    }

    get cropStyle() {
        return this._style?.crop;
    }

    get varietyStyle() {
        return this._style?.variety;
    }

    get color() {
        return this.varietyStyle?.Color || this.cropStyle?.Color;
    }

    get cssColor() {
        return colorToCss(this.color, false);
    }

    public checkDateIntersection(startDate: Date, endDate: Date): boolean {
        if (endDate.valueOf() < startDate.valueOf()) {
            return false;
        }

        if (this.cropping) {
            return checkDateIntersection(this.cropping.StartDate, this.cropping.EndDate, startDate, endDate);
        }
    }

    public checkCroppingDateIntersection(other: FieldViewModel): boolean {
        if (this.cropping && other.cropping) {
            return checkDateIntersection(
                this.cropping.StartDate,
                this.cropping.EndDate,
                other.cropping.StartDate,
                other.cropping.EndDate
            );
        }
    }

    isDisabledOnYear(year: number) {
        const years = this.field ? this.field.Years : null;
        return years && years.some((s) => s.YearId == year && s.Disabled);
    }
}

export function getVarietyName(c: ICropping) {
    return c?.Variety?.Name ?? c?.VarietyName;
}
